import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { erroreDi, giornoOra } from "@/lib/crm";

const APERTE = new Set(["programmato", "riprogrammato"]);

function quanti(n, uno, tanti) {
  if (!n) return null;
  return n === 1 ? `1 ${uno}` : `${n} ${tanti}`;
}

/** «1 preventivo e 2 contratti» */
function elenco(voci) {
  return voci.length < 2 ? voci.join("") : `${voci.slice(0, -1).join(", ")} e ${voci[voci.length - 1]}`;
}

/** Perché questo cliente non si elimina, dai dati che la scheda ha già. Decide comunque il
 *  database (0045): qui si evita solo di far premere un bottone che dirà di no. */
export function bloccoEliminazione(scheda, adesso = new Date()) {
  const d = scheda.documenti || {};
  const scritture = [
    quanti(d.preventivi?.length, "preventivo", "preventivi"),
    quanti(d.contratti?.length, "contratto", "contratti"),
    quanti(d.fatture?.length, "fattura", "fatture"),
  ].filter(Boolean);
  if (scritture.length) return { tipo: "documenti", testo: elenco(scritture) };
  const visita = (scheda.appuntamenti || [])
    .filter((a) => APERTE.has(a.stato) && new Date(a.data_inizio) > adesso)
    .sort((a, b) => new Date(a.data_inizio) - new Date(b.data_inizio))[0];
  return visita ? { tipo: "visita", visita } : null;
}

/** Elimina per sempre un cliente: solo il titolare, col codice (0045, D15). */
export function EliminaClienteDialog({ open, onOpenChange, scheda, onSegnaPerso, onCambiata, ritorno }) {
  const navigate = useNavigate();
  const versoPerso = useRef(false);
  const [busy, setBusy] = useState(false);
  const [rifiuto, setRifiuto] = useState(null);
  useEffect(() => {
    if (!open) setRifiuto(null);
  }, [open]);

  const c = scheda.cliente;
  // Vince la scheda (dopo un rifiuto si ricarica); il rifiuto conta da solo finché la scheda non dice niente.
  const blocco = bloccoEliminazione(scheda) || (rifiuto?.codice === "ha_documenti" ? { tipo: "documenti" } : null);
  const chiudi = (v) => !v && !busy && onOpenChange(false);

  const elimina = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/crm/clienti/${c.id}`);
      if (data?.file_rimasti) {
        toast.warning(`Cliente eliminato, ma ${data.file_rimasti} file dell'archivio non si sono tolti: avvisa chi segue il gestionale.`);
      } else {
        toast.success("Cliente eliminato");
      }
      onOpenChange(false);
      navigate("/clienti", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setRifiuto({ codice: d?.codice, testo: erroreDi(err) });
      // Qualcosa è cambiato da quando la scheda si è aperta: ricaricata, il dialogo mostra il blocco giusto.
      if (err?.response?.status === 409) onCambiata?.();
    } finally {
      setBusy(false);
    }
  };

  let titolo;
  let corpo;
  let azione;
  if (blocco?.tipo === "documenti") {
    titolo = `${c.nome_visualizzato} non si elimina`;
    corpo = (
      <DialogDescription>
        {blocco.testo
          ? `Ha ${blocco.testo}: preventivi, contratti e fatture restano nello storico. ${
              c.perso_at
                ? "È già segnato perso, quindi è fuori dalle liste di lavoro."
                : "Se il lavoro non va avanti, puoi segnarlo perso: esce dalle liste di lavoro."
            }`
          : rifiuto.testo}
      </DialogDescription>
    );
    azione = c.perso_at ? null : (
      <Button
        type="button"
        className="h-11 sm:h-9"
        onClick={() => {
          versoPerso.current = true;
          onOpenChange(false);
          onSegnaPerso();
        }}
        data-testid="elimina-segna-perso"
      >
        Segna perso
      </Button>
    );
  } else if (blocco?.tipo === "visita") {
    titolo = "Prima annulla la visita";
    corpo = (
      <DialogDescription>
        {c.nome_visualizzato} ha una visita in agenda: {giornoOra(blocco.visita.data_inizio)}. Annullala dal Calendario: i promemoria
        WhatsApp non partono e l'orario su Cal si libera. Poi torni qui.
      </DialogDescription>
    );
    azione = (
      <Button asChild className="h-11 sm:h-9">
        <Link to={`/calendario?appuntamento=${blocco.visita.id}`} data-testid="elimina-apri-visita">
          Apri la visita
        </Link>
      </Button>
    );
  } else {
    titolo = `Eliminare ${c.nome_visualizzato}?`;
    corpo = (
      <>
        <DialogDescription>Non si torna indietro. Spariscono per sempre:</DialogDescription>
        <ul className="grid list-disc gap-micro pl-group text-sm text-foreground" data-testid="elimina-cosa-sparisce">
          <li>la scheda, con telefono, email e note</li>
          <li>le visite passate e quelle annullate</li>
          <li>le assistenze</li>
          <li>i documenti caricati nell'archivio</li>
          <li>i calcoli salvati dal Calcolatore</li>
        </ul>
        <p className="text-sm text-muted-foreground">
          Restano le conversazioni WhatsApp e le prenotazioni arrivate da Cal, non più collegate a una scheda.
        </p>
        {rifiuto ? (
          <p role="alert" className="text-sm font-medium text-destructive" data-testid="elimina-rifiuto">
            {rifiuto.testo}
          </p>
        ) : null}
      </>
    );
    azione = (
      <Button
        type="button"
        variant="destructive"
        className="h-11 sm:h-9"
        disabled={busy}
        onClick={elimina}
        data-testid="elimina-conferma"
      >
        {busy ? "Elimino…" : "Elimina per sempre"}
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={chiudi}>
      <DialogContent
        className="sm:max-w-md"
        data-testid="elimina-modal"
        // Aperto senza DialogTrigger: chiuso, il fuoco torna su «Elimina»; se si passa a «Segna perso», lo prende quel dialogo.
        onCloseAutoFocus={(e) => {
          if (!versoPerso.current && ritorno?.current) {
            e.preventDefault();
            ritorno.current.focus();
          }
          versoPerso.current = false;
        }}
      >
        <div className="grid gap-card">
          <DialogHeader className="pr-card">
            <DialogTitle>{titolo}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-group">{corpo}</div>
          <DialogFooter className="gap-inside">
            <Button type="button" variant="outline" className="h-11 sm:h-9" disabled={busy} onClick={() => onOpenChange(false)}>
              {blocco ? "Chiudi" : "Annulla"}
            </Button>
            {azione}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
